import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Check, X, ExternalLink, Clock, Calendar, Award } from 'lucide-react';

// Import Shared Components
import { AISnippetBlock, formatQuickAnswer, generateKeyPoints } from '@/components/AISnippetBlock';
import { PeopleAlsoAsk, DEFAULT_IPTV_FAQ } from '@/components/PeopleAlsoAsk';
import { BreadcrumbNav, createBlogBreadcrumbs } from '@/components/BreadcrumbNav';
import { SchemaGenerator } from '@/components/SchemaGenerator';
import { DEEP_LINKING_MAP, getRelatedArticles } from '@/components/BlogArticleContent';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';
import { getCurrentMonthYear } from '@/services/eliteSummaryGenerator';
import { TableOfContents } from '@/components/TableOfContents';
import { MarkdownContentRenderer } from '@/components/MarkdownContentRenderer';
import { loadAllArticles } from '@/services/articleLoader';
import { Footer } from '@/components/Footer';
import { ArticleFooter } from '@/components/ArticleFooter';
import '@/styles/eliteDesignSystem.css';

/**
 * ReviewLayout - Specialized for Product and Service Reviews
 */
export const ReviewLayout = ({ article }) => {
    if (!article) return null;

    const [featuredImage, setFeaturedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [allArticles, setAllArticles] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles?.length > 0) setAllArticles(articles);
            } catch (e) {
                console.warn('Error loading articles', e);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        const fetchImage = async () => {
            try {
                setImageLoading(true);
                const searchQuery = getOptimizedSearchQuery(article.title + " review");
                const images = await getArticleImages(searchQuery, 1);
                if (images.length > 0) setFeaturedImage(images[0]);
            } catch (e) {
                console.error(e);
            } finally {
                setImageLoading(false);
            }
        };
        fetchImage();
    }, [article.title, article.id]);

    const quickAnswer = article.quickAnswer || formatQuickAnswer(article);
    const keyPoints = article.keyPoints || generateKeyPoints(article);
    const faqItems = article.faqItems || DEFAULT_IPTV_FAQ;
    const lastUpdated = getCurrentMonthYear();
    const breadcrumbs = createBlogBreadcrumbs(article);

    const reviewScore = article.reviewScore || 9.5;
    const reviewVerdict = article.reviewVerdict || "Excellent Choice for 2026";
    const pros = article.pros || ["High Quality", "Reliable", "Great Support"];
    const cons = article.cons || ["Limited trial period"];

    return (
        <>
            <TableOfContents article={article} />
            <SchemaGenerator
                article={{
                    ...article,
                    datePublished: article.date,
                    dateModified: `2026-01-${String(Math.min(article.id + 10, 31)).padStart(2, '0')}`,
                    keywords: `${article.category}, review, ${article.title.toLowerCase()}`
                }}
                faqItems={faqItems}
                breadcrumbs={breadcrumbs}
                type="Review"
            />

            <div className="min-h-screen bg-background">
                {/* HERO SECTION */}
                <section className="relative pt-24 pb-12 px-4 overflow-hidden border-b border-white/5">
                    <div className="absolute top-0 right-0 w-full h-full max-w-7xl mx-auto overflow-hidden">
                        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-[120px]" />
                    </div>

                    <div className="relative max-w-5xl mx-auto z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-xs font-bold text-yellow-400 uppercase tracking-wider mb-6">
                                <Award className="w-3 h-3" /> Official Review
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-white leading-[1.1]">
                                {article.title}
                            </h1>

                            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                                {article.excerpt}
                            </p>

                            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                                <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {article.readTime}</span>
                                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {article.date}</span>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <div className="max-w-5xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <main className="lg:col-span-8">
                        {/* SCORE CARD */}
                        <div className="mb-10 p-6 rounded-2xl bg-card border border-white/10 shadow-xl overflow-hidden relative">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                <div className="flex-shrink-0 text-center">
                                    <div className="w-32 h-32 rounded-full border-4 border-yellow-500 flex items-center justify-center bg-yellow-500/10">
                                        <span className="text-4xl font-bold text-white">{reviewScore}</span>
                                    </div>
                                    <div className="flex items-center justify-center gap-1 mt-2 text-yellow-500">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className={`w-4 h-4 ${i < Math.floor(reviewScore / 2) ? 'fill-current' : 'opacity-30'}`} />
                                        ))}
                                    </div>
                                </div>
                                <div className="flex-grow">
                                    <h3 className="text-2xl font-bold text-white mb-2">{reviewVerdict}</h3>
                                    <p className="text-muted-foreground mb-4">
                                        Our editorial team has rigorously tested this service. Here is our breakdown of where it excels and where it could improve.
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <h4 className="font-semibold text-green-400 text-sm uppercase">Pros</h4>
                                            {pros.map((pro, i) => (
                                                <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                                    <Check className="w-4 h-4 text-green-500 mt-0.5" /> {pro}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="space-y-2">
                                            <h4 className="font-semibold text-red-400 text-sm uppercase">Cons</h4>
                                            {cons.map((con, i) => (
                                                <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                                    <X className="w-4 h-4 text-red-500 mt-0.5" /> {con}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FEATURED IMAGE */}
                        {featuredImage && (
                            <div className="mb-10 rounded-xl overflow-hidden border border-white/10">
                                <img src={featuredImage.url} alt={article.title} className="w-full aspect-video object-cover" />
                            </div>
                        )}

                        {/* CONTENT BODY */}
                        <div className="prose prose-invert prose-lg max-w-none prose-headings:text-yellow-50 text-slate-300">
                            <MarkdownContentRenderer content={article.content} />
                        </div>

                        {/* PEOPLE ALSO ASK */}
                        <div className="mt-12">
                            <PeopleAlsoAsk faqItems={faqItems} title="Specific Questions" variant="default" />
                        </div>

                        {/* Article Footer - Share & Navigation */}
                        <ArticleFooter
                            title={article.title}
                            nextArticleTitle="Best IPTV USA 2026"
                            nextArticleUrl="/blog/best-iptv-usa-2026"
                        />
                    </main>

                    {/* SIDEBAR */}
                    <aside className="hidden lg:block lg:col-span-4 space-y-8">
                        <div className="sticky top-24">
                            <div className="p-6 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/10 border border-yellow-500/20 text-center">
                                <h4 className="font-bold text-white mb-2 text-xl">Our Top Pick</h4>
                                <div className="text-4xl font-bold text-yellow-400 mb-2">{reviewScore}/10</div>
                                <p className="text-sm text-muted-foreground mb-6">Rated Excellent for Stability & Quality</p>
                                <a href="https://xtreamcube.com/freetrialiptv/" className="block w-full py-3 bg-yellow-500 text-black rounded-lg font-bold hover:bg-yellow-400 transition shadow-lg shadow-yellow-500/20">
                                    Get Free Trial
                                </a>
                                <p className="text-xs text-muted-foreground mt-3">No credit card required</p>
                            </div>
                        </div>
                    </aside>
                </div>
                {/* Footer moved to BlogArticlePage */}
            </div>
        </>
    );
};
